import { TriggerController } from 'Components/controllers/trigger-controller';
import 'Components/vm-icon/vm-icon';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { lockScroll, unlockScroll } from 'Utils/dom';
import Styles from './vm-modal.scss';

/**
 * A modal dialog component built on the native `<dialog>` element.
 * Supports backdrop click to close, Escape key, and a close button in the header.
 *
 * @slot - Default slot for the modal body content.
 * @slot header - Slot for header content displayed next to the close button.
 * @slot footer - Optional footer pinned to the bottom of the modal (e.g. action buttons).
 *                Empty by default — the footer area collapses when nothing is slotted in.
 *
 * Set `variant="lightbox"` for a transparent, centered media stage with a floating
 * close button: full-bleed below 1024px, content-sized and bounded on desktop.
 * The lightbox doesn't display the `header` slot, but its content still names the
 * dialog, so always provide one. The lightbox dialog clips
 * its content (`overflow: hidden`), so `--vm-modal-overflow` has no effect there.
 *
 * @csspart modal        - The `<dialog>` element. Style this for sizing / shape (height, etc.).
 * @csspart container    - The layout container inside the dialog. Style this to tweak inner padding.
 * @csspart header       - The header bar wrapping the header slot + close button.
 * @csspart close-button - The X button in the header. Hide via `display: none` when the modal
 *                         is dismissed exclusively through consumer-rendered buttons.
 * @csspart body         - The scrollable body area wrapping the default slot.
 * @csspart footer       - The sticky footer wrapping the footer slot. Its `box-shadow` is driven
 *                         by an animation where scroll-driven timelines are supported, so
 *                         overriding `box-shadow` from outside needs `!important`.
 *
 * @fires vm-close - Fired when the modal dismisses. Fires on backdrop click, the built-in
 *                   close button, Escape, a consumer-driven `requestClose()`
 *                   call, and any programmatic `open = false`.
 *
 * @example
 * ```html
 * <button id="open-info">More info</button>
 *
 * <vm-modal trigger="#open-info">
 *   <span slot="header">More info</span>
 *   <p>Modal body content here</p>
 * </vm-modal>
 * ```
 */
@customElement('vm-modal')
export class VmModal extends LitElement {
    static override styles = Styles;

    /** Whether the modal is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** Hides the backdrop and disables close-on-outside-click. */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop' })
    disableBackdrop = false;

    /**
     * Override for the native `<dialog>` `closedby` attribute. When unset, derived
     * from `disableBackdrop` (`"closerequest"` if true, `"any"` otherwise). Set to
     * `"none"` to disable both backdrop and Escape — the only dismiss path then is
     * a programmatic `open = false` from the consumer.
     */
    @property()
    closedby?: 'any' | 'closerequest' | 'none';

    /** Card or lightbox presentation; both share the same dialog lifecycle. */
    @property({ reflect: true })
    variant: 'default' | 'lightbox' = 'default';

    /**
     * CSS selector for an external element that opens the modal on click.
     * The element must exist in the DOM when the modal connects — binding
     * is one-shot, not observed.
     */
    @property()
    trigger?: string;

    @state()
    private _closing = false;

    @state()
    private _hasFooter = false;

    @query('dialog')
    private _dialog!: HTMLDialogElement;

    /** Whether this instance holds one count of the shared scroll lock. */
    private _holdsScrollLock = false;

    private _triggerCtrl = new TriggerController(this, {
        getSelector: () => this.trigger,
        onActivate: () => {
            this.open = true;
        },
    });

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._releaseScrollLock();
    }

    // The scroll lock is shared and reference-counted across overlays. Release only the
    // count this modal took, and only once — a modal disconnected twice while open (moved,
    // then removed) must not drop another overlay's lock.
    private _takeScrollLock(): void {
        if (this._holdsScrollLock) return;
        this._holdsScrollLock = true;
        lockScroll();
    }

    private _releaseScrollLock(): void {
        if (!this._holdsScrollLock) return;
        this._holdsScrollLock = false;
        unlockScroll();
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has('open')) {
            this._triggerCtrl.setExpanded(this.open);
            if (this.open) {
                this._closing = false;
                this._takeScrollLock();
                this.updateComplete.then(() => {
                    // The host can be removed between scheduling this microtask and its
                    // resolution (common in tests that mount → toggle open → unmount in
                    // the same tick). `showModal()` throws InvalidStateError on a dialog
                    // that isn't in a Document, so skip if we've been detached.
                    if (this._dialog && !this._dialog.open && this._dialog.isConnected) {
                        this._dialog.showModal();
                    }
                });
            } else if (changed.get('open') === true) {
                // Only unlock on a real true→false transition — skip the initial
                // undefined→false render so we don't clobber another overlay's lock.
                this._releaseScrollLock();
                // Closing destroys the footer slot; reopening builds a new one. An empty
                // slot fires no slotchange, so reset here or the next open shows an empty
                // footer bar left over from this one.
                this._hasFooter = false;
                // Dispatch vm-close from here so it fires on every close path,
                // including programmatic open=false (not just the animated close path).
                this.dispatchEvent(new CustomEvent('vm-close', { bubbles: true, composed: true }));
                // Restore focus to the trigger so keyboard/SR users don't lose their place.
                this._triggerCtrl.focus();
            }
        }
    }

    private _handleCancel(e: Event): void {
        // Native cancel fires on Escape and (via closedby="any") on backdrop click.
        // Prevent the default close so we can animate first.
        e.preventDefault();
        this._animateClose();
    }

    private _animateClose(): void {
        this._closing = true;
    }

    /**
     * Dismiss through the same animated close path as the built-in close button.
     */
    requestClose(): void {
        this._animateClose();
    }

    private _onFooterSlotChange = (e: Event): void => {
        // A wrapper component can assign its own <slot> here to forward its light DOM on.
        // `flatten: true` looks through that to the real content, so an empty forwarding
        // slot doesn't count as a footer.
        this._hasFooter = (e.target as HTMLSlotElement).assignedElements({ flatten: true }).length > 0;
    };

    private _onAnimationEnd(e: AnimationEvent): void {
        // The footer's scroll shadow animates too, and animationend bubbles — only the
        // dialog's own close animation should end the close.
        if (e.target !== e.currentTarget) return;
        if (!this._closing) return;
        this._closing = false;
        this._dialog?.close();
        // Setting open=false triggers updated(), which handles vm-close, focus,
        // and scroll-unlock — single source of truth for the close lifecycle.
        this.open = false;
    }

    private _onNativeClose(e: Event): void {
        // The browser can close the dialog without our animation (e.g. a quick second
        // Escape), so finish closing here. Our own close path already set open=false.
        // A late `close` from a previous dialog (the modal was reopened) is ignored.
        if (!this.open || e.target !== this._dialog) return;
        this._closing = false;
        this.open = false;
    }

    // Fallback for browsers without `closedby` support (e.g. Safari at the time of writing).
    // Dialog padding is moved to .vm-modal__container so any click landing on the dialog
    // itself is on the backdrop, not on internal padding.
    private _onBackdropClick(e: MouseEvent): void {
        if (this.disableBackdrop) return;
        if (e.target === this._dialog) {
            this._animateClose();
        }
    }

    override render() {
        if (!this.open) return nothing;

        return html`
            <dialog
                part="modal"
                class="vm-modal ${this._closing ? 'vm-modal--closing' : ''}"
                aria-labelledby="vm-modal-title"
                closedby=${this.closedby ?? (this.disableBackdrop ? 'closerequest' : 'any')}
                @cancel=${this._handleCancel}
                @close=${this._onNativeClose}
                @click=${this._onBackdropClick}
                @animationend=${this._onAnimationEnd}
            >
                <div part="container" class="vm-modal__container">
                    <div part="header" class="vm-modal__header">
                        <div id="vm-modal-title" class="vm-modal__header-content">
                            <slot name="header"></slot>
                        </div>
                        <button
                            part="close-button"
                            class="vm-modal__close-btn"
                            @click=${this._animateClose}
                            aria-label="Close"
                        >
                            <vm-icon name="x-close" size="md"></vm-icon>
                        </button>
                    </div>
                    <div part="body" class="vm-modal__body">
                        <slot></slot>
                    </div>
                    <div part="footer" class="vm-modal__footer ${this._hasFooter ? '' : 'vm-modal__footer--empty'}">
                        <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
                    </div>
                </div>
            </dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-modal': VmModal;
    }
    interface HTMLElementEventMap {
        'vm-close': CustomEvent;
    }
}
